import { Memoize, isEmpty, parseIfString } from '@springbokagency/stencil-base';
import { Component, Host, h, Prop, State, Listen } from '@stencil/core';

import checkWindowSize from '../../../utils/checkWindowSize';

import type { LandingPagePropType } from './landing-page.types';

/**
 * Component containing the whole landing page
 */
@Component({
  tag: 'efb-landing-page',
  styleUrl: 'landing-page.scss',
  shadow: true,
})
export class EfbLandingPage {
  /**
   * State
   */

  @State() isHeaderAlwaysVisible = !checkWindowSize().isMobile;

  /**
   * Landing page's content data.
   */
  @Prop() data: LandingPagePropType | string;

  @Memoize('data')
  get parsedData(): LandingPagePropType {
    return parseIfString(this.data);
  }

  /**
   * Events
   */

  @Listen('resize', { target: 'window' })
  handleWindowResize() {
    this.isHeaderAlwaysVisible = !checkWindowSize().isMobile;
  }

  /**
   * Render
   */

  render() {
    return (
      <Host>
        <efb-default-layout
          class="landing-page-layout"
          data={this.parsedData?.layout}
          videos={this.parsedData?.videos}
        >
          <div class="landing-page">
            {/* Hero */}
            {!isEmpty(this.parsedData?.hero) && <efb-landing-hero data={this.parsedData.hero} />}

            {/* Intro */}
            {!isEmpty(this.parsedData?.intro) && (
              <efb-section>
                {/* Text */}
                <div class="centered-text">
                  <efb-title
                    text={this.parsedData.intro.title}
                    tag="h2"
                    size="sm"
                    bottomMargin="md"
                    bold
                  />
                  <efb-paragraph
                    style={{ maxWidth: '726px' }}
                    text={this.parsedData.intro.text}
                    size="md"
                    bottomMargin="lg"
                  />
                </div>
              </efb-section>
            )}

            {/* Failsurance */}
            {!isEmpty(this.parsedData.failsurance) && (
              <efb-section id="failsurance" contentSize="lg">
                <efb-failsurance-card
                  title-text={this.parsedData.failsurance.titleText}
                  subtitle={this.parsedData.failsurance.subtitle}
                  text={this.parsedData.failsurance.text}
                  button={this.parsedData.failsurance.button}
                  legalMention={this.parsedData.failsurance.legalMention}
                />
              </efb-section>
            )}

            {/* Packs */}
            {!isEmpty(this.parsedData?.packs) && (
              <efb-section id="packs">
                {/* Text */}
                <efb-title
                  style={{ maxWidth: '522px' }}
                  text={this.parsedData.packs.title}
                  tag="h2"
                  size="sm"
                  bottomMargin="md"
                  bold
                />
                <efb-paragraph
                  style={{ maxWidth: '726px' }}
                  text={this.parsedData.packs.text}
                  size="md"
                  bottomMargin="lg"
                />

                {/* Cards */}
                <ul class="pack-card-list">
                  {this.parsedData.packs.cards.map((item, index) => (
                    <li key={index} class="item">
                      <efb-pack-card {...item} />
                    </li>
                  ))}
                </ul>

                {/* Hub Youth */}
                {!isEmpty(this.parsedData.hubYouth) && (
                  <efb-hub-youth-card
                    title-text={this.parsedData.hubYouth.titleText}
                    text={this.parsedData.hubYouth.text}
                    button={this.parsedData.hubYouth.button}
                  />
                )}
              </efb-section>
            )}

            {/* Flora banner */}
            {!isEmpty(this.parsedData.floraBanner) && (
              <efb-flora-banner
                title-text={this.parsedData.floraBanner.titleText}
                text={this.parsedData.floraBanner.text}
                usp={this.parsedData.floraBanner.usp}
                highlightTitle={this.parsedData.floraBanner.highlightTitle}
                button={this.parsedData.floraBanner.button}
              />
            )}

            {/* Masterclass Falen */}
            {this.parsedData.masterclass && (
              <efb-section id="masterclass" background="purple" contentSize="lg">
                {/* Text */}
                <div style={{ maxWidth: '440px', margin: '0 auto', textAlign: 'center' }}>
                  <efb-title-bar
                    text={this.parsedData.masterclass.title}
                    tag="h2"
                    variant="pink"
                    bottomMargin="sm"
                  />
                  <efb-paragraph text={this.parsedData.masterclass.text} bottomMargin="lg" />
                </div>

                {/* Videos */}
                <ul
                  class={`video-card-list ${
                    this.parsedData.masterclass?.videos?.length === 1 ? 'has-one-item' : ''
                  } `}
                >
                  {this.parsedData.masterclass.videos.map((item, index) => (
                    <li key={index} class="item">
                      <efb-video-block {...item} ratio="portrait" />
                    </li>
                  ))}
                </ul>
              </efb-section>
            )}

            {/* FAQ */}
            {!isEmpty(this.parsedData.faq) && (
              <efb-faq-section id="covered" class="article-section" data={this.parsedData.faq} />
            )}

            {/* Article section */}
            {!isEmpty(this.parsedData?.articles) && (
              <efb-article-section
                id="articles"
                class="article-section"
                data={this.parsedData.articles}
              />
            )}

            {/* External Article section */}
            {!isEmpty(this.parsedData.externalArticles) && (
              <efb-external-article-section
                class="article-section"
                data={this.parsedData.externalArticles}
              />
            )}

            {/* Stefaan legt uit */}
            <efb-section id="explained" background="pink">
              <efb-video-thumbnail-group data={this.parsedData.explained} theme="light" />
            </efb-section>

            {/*  Is dit verzekerd? */}
            <efb-section>
              <efb-video-thumbnail-group data={this.parsedData.covered} theme="light" />
            </efb-section>
          </div>
        </efb-default-layout>
      </Host>
    );
  }
}
